import styled, { keyframes } from 'styled-components';
import { ThemeType } from '../../types/command';
import { themeTokens } from '../../styles/theme';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const blink = keyframes`
  50% { border-color: transparent; }
`;

// Page container
export const ClassicPageContainer = styled.div<{ $themeName: ThemeType }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bg};
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
  overflow-x: hidden;
  -webkit-tap-highlight-color: transparent;
`;

// ========== HEADER ==========
export const Header = styled.header<{ $themeName: ThemeType; $scrolled: boolean }>`
  width: 100%;
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  background-color: ${({ $themeName }) => `${themeTokens[$themeName].bg}dd`};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  transition: box-shadow 0.3s ease;
  box-shadow: ${({ $scrolled }) => $scrolled ? '0 2px 20px rgba(0, 0, 0, 0.3)' : 'none'};
`;

export const HeaderContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const HeaderLogo = styled.span<{ $themeName: ThemeType }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  white-space: nowrap;
`;

export const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled.a<{ $themeName: ThemeType; $active?: boolean }>`
  padding: 8px 14px;
  font-size: 14px;
  color: ${({ $themeName, $active }) =>
    $active ? themeTokens[$themeName].accent : themeTokens[$themeName].textSecondary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
  position: relative;
  user-select: none;

  &::after {
    content: '';
    position: absolute;
    bottom: 2px;
    left: 14px;
    right: 14px;
    height: 2px;
    background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    transform: scaleX(${({ $active }) => $active ? 1 : 0});
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HeaderButton = styled.button<{ $themeName: ThemeType; $variant?: 'primary' | 'ghost' }>`
  padding: 8px 16px;
  font-family: inherit;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].accent};
  background: ${({ $variant, $themeName }) =>
    $variant === 'primary' ? themeTokens[$themeName].accent : 'transparent'};
  color: ${({ $variant, $themeName }) =>
    $variant === 'primary'
      ? ($themeName === 'powershell' ? themeTokens[$themeName].bg : '#fff')
      : themeTokens[$themeName].accent};

  &:hover {
    background: ${({ $themeName }) => themeTokens[$themeName].accent};
    color: ${({ $themeName }) =>
      $themeName === 'powershell' ? themeTokens[$themeName].bg : '#fff'};
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

// Mobile menu toggle
export const MenuToggle = styled.button<{ $themeName: ThemeType; $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 20px;
  padding: 0;
  position: relative;

  span {
    display: block;
    width: 100%;
    height: 2px;
    background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    position: absolute;
    left: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;

    &:nth-child(1) {
      top: 0;
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(45deg) translate(6px, 6px)' : 'none'};
    }
    &:nth-child(2) {
      top: 9px;
      opacity: ${({ $isOpen }) => $isOpen ? 0 : 1};
    }
    &:nth-child(3) {
      top: 18px;
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(-45deg) translate(6px, -6px)' : 'none'};
    }
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Mobile overlay menu
export const MobileMenuOverlay = styled.div<{ $themeName: ThemeType; $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: ${({ $themeName }) => `${themeTokens[$themeName].bg}f5`};
    backdrop-filter: blur(20px);
    z-index: 999;
    opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
    pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
    transition: opacity 0.3s ease;
  }
`;

export const MobileNavLink = styled.a<{ $themeName: ThemeType }>`
  font-size: 24px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  text-decoration: none;
  padding: 12px 24px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

// ========== HERO ==========
export const HeroSection = styled.section<{ $themeName: ThemeType }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 24px 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
      ${({ $themeName }) => themeTokens[$themeName].border} 1px,
      transparent 1px
    );
    background-size: 30px 30px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

export const HeroPrompt = styled.div<{ $themeName: ThemeType }>`
  font-size: 16px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin-bottom: 8px;
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

export const HeroName = styled.h1<{ $themeName: ThemeType }>`
  font-size: 48px;
  font-weight: 700;
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
  margin: 0 0 16px 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 36px;
  }
`;

export const HeroTagline = styled.p<{ $themeName: ThemeType }>`
  font-size: 20px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0 0 40px 0;
  text-align: center;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

export const HeroCTA = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
`;

// ========== CONTENT ==========
export const ContentContainer = styled.main`
  width: 100%;
  padding: 0 24px;

  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

// ========== SECTION TITLE ==========
export const SectionTitle = styled.h2<{ $themeName: ThemeType }>`
  font-size: 28px;
  margin: 0 0 32px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid ${({ $themeName }) => themeTokens[$themeName].accent};
  display: inline-block;

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

export const SectionPrompt = styled.span<{ $themeName: ThemeType }>`
  display: block;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin-bottom: 8px;
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

// ========== SKILLS ==========
export const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const SkillCategory = styled.div<{ $themeName: ThemeType }>`
  padding: 24px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
`;

export const SkillCategoryTitle = styled.h3<{ $themeName: ThemeType }>`
  font-size: 15px;
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
`;

export const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const SkillTag = styled.span<{ $themeName: ThemeType; $delay?: number }>`
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
  border-radius: 3px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgTertiary};
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};

  &::before {
    content: '> ';
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
    opacity: 0.6;
  }
`;

// ========== EXPERIENCE TIMELINE ==========
export const Timeline = styled.div<{ $themeName: ThemeType }>`
  position: relative;
  padding-left: 32px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

export const TimelineItem = styled.div<{ $themeName: ThemeType }>`
  position: relative;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }

  &::before {
    content: '';
    position: absolute;
    left: -38px;
    top: 30px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    border: 2px solid ${({ $themeName }) => themeTokens[$themeName].bg};
  }
`;

export const TimelineTitle = styled.h3<{ $themeName: ThemeType }>`
  font-size: 18px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  margin: 0 0 4px 0;
`;

export const TimelineCompany = styled.span<{ $themeName: ThemeType }>`
  font-size: 15px;
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
`;

export const TimelinePeriod = styled.span<{ $themeName: ThemeType }>`
  display: block;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin: 4px 0 12px 0;
`;

export const TimelineDescription = styled.p<{ $themeName: ThemeType }>`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0 0 12px 0;
`;

// ========== PROJECTS ==========
export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ProjectCard = styled.div<{ $themeName: ThemeType }>`
  padding: 24px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $themeName }) => themeTokens[$themeName].accent};
    box-shadow: 0 4px 20px ${({ $themeName }) => themeTokens[$themeName].shadow};
  }
`;

export const ProjectTitle = styled.h3<{ $themeName: ThemeType }>`
  font-size: 16px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  margin: 0 0 8px 0;

  &::before {
    content: '$ cat ';
    color: ${({ $themeName }) => themeTokens[$themeName].muted};
    font-size: 13px;
    font-weight: 400;
  }
`;

export const ProjectDescription = styled.p<{ $themeName: ThemeType }>`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  margin: 0 0 16px 0;
`;

// ========== EDUCATION ==========
export const EducationCard = styled.div<{ $themeName: ThemeType }>`
  padding: 20px 24px;
  margin-bottom: 16px;
  border-radius: 8px;
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
  border: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
`;

export const EducationDegree = styled.h3<{ $themeName: ThemeType }>`
  font-size: 16px;
  color: ${({ $themeName }) => themeTokens[$themeName].text};
  margin: 0 0 4px 0;
`;

export const EducationInstitution = styled.span<{ $themeName: ThemeType }>`
  font-size: 14px;
  color: ${({ $themeName }) => themeTokens[$themeName].accent};
`;

export const EducationPeriod = styled.span<{ $themeName: ThemeType }>`
  display: block;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  margin-top: 4px;
`;

// ========== FOOTER ==========
export const FooterContainer = styled.footer<{ $themeName: ThemeType }>`
  width: 100%;
  margin-top: 64px;
  border-top: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  background-color: ${({ $themeName }) => themeTokens[$themeName].bgSecondary};
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 32px 24px;
  }
`;

export const FooterBrand = styled.div<{ $themeName: ThemeType }>`
  h3 {
    font-size: 20px;
    margin: 0 0 8px 0;
    color: ${({ $themeName }) => themeTokens[$themeName].text};
  }

  p {
    font-size: 14px;
    color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
    margin: 0 0 16px 0;
    line-height: 1.6;
  }
`;

export const FooterPrompt = styled.span<{ $themeName: ThemeType }>`
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
  font-family: ${({ $themeName }) => themeTokens[$themeName].font};
`;

export const FooterColumn = styled.div<{ $themeName: ThemeType }>`
  h4 {
    font-size: 15px;
    margin: 0 0 16px 0;
    color: ${({ $themeName }) => themeTokens[$themeName].text};
  }
`;

export const FooterLink = styled.a<{ $themeName: ThemeType }>`
  display: block;
  font-size: 14px;
  color: ${({ $themeName }) => themeTokens[$themeName].textSecondary};
  text-decoration: none;
  padding: 4px 0;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    color: ${({ $themeName }) => themeTokens[$themeName].accent};
  }
`;

export const FooterBottom = styled.div<{ $themeName: ThemeType }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 24px;
  border-top: 1px solid ${({ $themeName }) => themeTokens[$themeName].border};
  text-align: center;
  font-size: 13px;
  color: ${({ $themeName }) => themeTokens[$themeName].muted};
`;

// ========== TERMINAL FAB ==========
export const TerminalFAB = styled.button<{ $themeName: ThemeType }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background-color: ${({ $themeName }) => themeTokens[$themeName].accent};
  color: ${({ $themeName }) => $themeName === 'powershell' ? themeTokens[$themeName].bg : '#FFFFFF'};
  border: none;
  border-radius: ${({ $themeName }) => $themeName === 'ubuntu' ? '50%' : $themeName === 'powershell' ? '0' : '8px'};
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 4px 12px ${({ $themeName }) => themeTokens[$themeName].shadow};
  z-index: 900;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${({ $themeName }) => themeTokens[$themeName].shadowStrong};
  }

  &:active {
    transform: translateY(0);
  }
`;
